import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { toast } from 'sonner';
import { BRANDING } from '../config/branding';
import { 
  User, Building2, CreditCard, FileText, Key, Plus, ExternalLink, 
  Download, Check, X, AlertTriangle, RefreshCw, Calendar, Eye, EyeOff,
  Mail, Phone, MapPin, Building, ChevronRight, Shield, Zap, Clock,
  TrendingUp, ArrowRight, Sparkles, Crown, AlertCircle, Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';

const AccountPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accountData, setAccountData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    fetchAccountData();
  }, []);

  const fetchAccountData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/account');
      setAccountData(data);
      setAccessDenied(false);
    } catch (error) {
      if (error.response?.data?.code === 'NOT_ACCOUNT_OWNER') {
        setAccessDenied(true);
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-stone-50 to-amber-50/30">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-stone-500">Chargement de votre espace...</p>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8 bg-gradient-to-br from-stone-50 to-red-50/20">
        <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-6">
          <Shield className="w-10 h-10 text-red-500" />
        </div>
        <h1 className="text-2xl font-semibold text-stone-800 mb-2">Accès refusé</h1>
        <p className="text-stone-500 text-center max-w-md mb-8">
          Cette page est réservée aux propriétaires de compte SaaS. 
          Contactez votre administrateur si vous pensez qu'il s'agit d'une erreur.
        </p>
        <Button onClick={() => navigate('/')} className="bg-emerald-600 hover:bg-emerald-700">
          <ArrowRight className="w-4 h-4 mr-2" />
          Retour au tableau de bord
        </Button>
      </div>
    );
  }

  if (!accountData) return null;

  const { account, owner, subscription, limits } = accountData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/20">
      {/* Premium Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-stone-200/60 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src={BRANDING.logo} 
                alt={BRANDING.name} 
                className="w-12 h-12 drop-shadow-sm" 
                data-testid="account-header-logo"
              />
              <div>
                <h1 className="text-xl font-bold text-stone-800">Mon Compte</h1>
                <p className="text-sm text-stone-500">{account?.company || account?.name}</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate('/')} className="gap-2">
              <Building2 className="w-4 h-4" />
              Acceder a mon riad
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Quick Overview Banner */}
        <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 text-white shadow-xl shadow-emerald-500/20">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5" />
                <span className="text-sm font-medium text-emerald-100">Plan actuel</span>
              </div>
              <h2 className="text-2xl font-bold mb-1">{subscription?.plan?.name || 'Standard'}</h2>
              <p className="text-emerald-100 text-sm">
                {subscription?.billing_cycle === 'YEARLY' ? 'Facturation annuelle' : 'Facturation mensuelle'}
              </p>
            </div>
            
            <div className="flex flex-wrap gap-4 lg:gap-8">
              <div className="bg-white/10 backdrop-blur rounded-xl px-5 py-3">
                <p className="text-xs text-emerald-100 mb-1">Prix</p>
                <p className="text-xl font-bold">
                  {(subscription?.price || 0).toLocaleString()} <span className="text-sm font-normal">{subscription?.currency}/mois</span>
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl px-5 py-3">
                <p className="text-xs text-emerald-100 mb-1">Riads</p>
                <p className="text-xl font-bold">
                  {limits?.current_tenants || 0} <span className="text-sm font-normal">/ {limits?.max_tenants === 0 ? '∞' : limits?.max_tenants}</span>
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl px-5 py-3">
                <p className="text-xs text-emerald-100 mb-1">Prochaine échéance</p>
                <p className="text-xl font-bold">
                  {subscription?.next_billing_date 
                    ? format(new Date(subscription.next_billing_date), 'dd MMM', { locale: fr })
                    : '-'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="bg-white/60 backdrop-blur border border-stone-200/60 p-1 rounded-xl shadow-sm">
            <TabsTrigger value="overview" className="rounded-lg gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <TrendingUp className="w-4 h-4" />
              Vue d'ensemble
            </TabsTrigger>
            <TabsTrigger value="subscription" className="rounded-lg gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <CreditCard className="w-4 h-4" />
              Abonnement
            </TabsTrigger>
            <TabsTrigger value="invoices" className="rounded-lg gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <FileText className="w-4 h-4" />
              Factures
            </TabsTrigger>
            <TabsTrigger value="riads" className="rounded-lg gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Building2 className="w-4 h-4" />
              Mes Riads
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Settings className="w-4 h-4" />
              Paramètres
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab accountData={accountData} onNavigate={setActiveTab} />
          </TabsContent>

          <TabsContent value="subscription">
            <SubscriptionTab subscription={subscription} limits={limits} />
          </TabsContent>

          <TabsContent value="invoices">
            <InvoicesTab />
          </TabsContent>

          <TabsContent value="riads">
            <RiadsTab limits={limits} onUpdate={fetchAccountData} />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsTab account={account} owner={owner} onUpdate={fetchAccountData} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// ============ OVERVIEW TAB ============
const OverviewTab = ({ accountData, onNavigate }) => {
  const { account, owner, subscription, limits } = accountData;
  const plan = subscription?.plan;
  const [alerts, setAlerts] = useState([]);
  const [accountLimits, setAccountLimits] = useState(null);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  
  // Calculate days until next billing
  const daysUntilBilling = subscription?.next_billing_date 
    ? differenceInDays(new Date(subscription.next_billing_date), new Date())
    : null;

  // Fetch alerts and limits
  useEffect(() => {
    const fetchAlertsAndLimits = async () => {
      try {
        const [alertsRes, limitsRes] = await Promise.all([
          api.get('/account/alerts'),
          api.get('/account/limits')
        ]);
        setAlerts(alertsRes.data.alerts || []);
        setAccountLimits(limitsRes.data.limits || null);
      } catch (error) {
        console.error('Error fetching alerts/limits:', error);
      } finally {
        setLoadingAlerts(false);
      }
    };
    fetchAlertsAndLimits();
  }, []);

  const getSeverityStyle = (severity) => ({
    critical: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-500', text: 'text-red-700' },
    warning: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-500', text: 'text-amber-700' },
    info: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-500', text: 'text-blue-700' }
  }[severity] || { bg: 'bg-stone-50', border: 'border-stone-200', icon: 'text-stone-500', text: 'text-stone-700' });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Column - Account & Quick Actions */}
      <div className="lg:col-span-2 space-y-6">
        {/* Alerts Section */}
        {alerts.length > 0 && (
          <div className="space-y-3">
            {alerts.slice(0, 3).map(alert => {
              const style = getSeverityStyle(alert.severity);
              return (
                <div 
                  key={alert.id} 
                  className={`p-4 rounded-xl border ${style.bg} ${style.border} flex items-start gap-3`}
                >
                  <div className={`flex-shrink-0 ${style.icon}`}>
                    {alert.severity === 'critical' ? <AlertTriangle className="w-5 h-5" /> : 
                     alert.severity === 'warning' ? <AlertCircle className="w-5 h-5" /> : 
                     <Clock className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-semibold text-sm ${style.text}`}>{alert.title}</h4>
                    <p className="text-sm text-stone-600 mt-0.5">{alert.message}</p>
                  </div>
                  {alert.action && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className={`flex-shrink-0 ${style.border} ${style.text}`}
                      onClick={() => {
                        if (alert.action.type === 'view_invoices') onNavigate('invoices');
                        else if (alert.action.type === 'upgrade') onNavigate('subscription');
                        else if (alert.action.type === 'setup') onNavigate('riads');
                      }}
                    >
                      {alert.action.label}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {/* Account Card */}
        <Card className="border-stone-200/60 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-stone-100 to-stone-50 px-6 py-4 border-b border-stone-200/60">
            <h3 className="font-semibold text-stone-800 flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-600" />
              Mon Compte
            </h3>
          </div>
          <CardContent className="p-6">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-bold text-emerald-600">
                  {owner?.first_name?.[0]}{owner?.last_name?.[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-lg font-semibold text-stone-800">{owner?.first_name} {owner?.last_name}</h4>
                <p className="text-stone-500 text-sm mb-3">{account?.company}</p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="flex items-center gap-1.5 text-stone-600">
                    <Mail className="w-4 h-4 text-stone-400" />
                    {owner?.email}
                  </span>
                  {owner?.phone && (
                    <span className="flex items-center gap-1.5 text-stone-600">
                      <Phone className="w-4 h-4 text-stone-400" />
                      {owner?.phone}
                    </span>
                  )}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => onNavigate('settings')} className="text-stone-500">
                Modifier
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button 
            onClick={() => onNavigate('invoices')}
            className="group p-4 bg-white rounded-xl border border-stone-200/60 hover:border-emerald-200 hover:shadow-md transition-all text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <p className="font-medium text-stone-800 text-sm">Mes factures</p>
            <p className="text-xs text-stone-400">Télécharger PDF</p>
          </button>

          <button 
            onClick={() => onNavigate('riads')}
            className="group p-4 bg-white rounded-xl border border-stone-200/60 hover:border-emerald-200 hover:shadow-md transition-all text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Building2 className="w-5 h-5 text-amber-600" />
            </div>
            <p className="font-medium text-stone-800 text-sm">Mes riads</p>
            <p className="text-xs text-stone-400">{limits?.current_tenants || 0} actif(s)</p>
          </button>

          <button 
            onClick={() => onNavigate('subscription')}
            className="group p-4 bg-white rounded-xl border border-stone-200/60 hover:border-emerald-200 hover:shadow-md transition-all text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <CreditCard className="w-5 h-5 text-emerald-600" />
            </div>
            <p className="font-medium text-stone-800 text-sm">Abonnement</p>
            <p className="text-xs text-stone-400">{subscription?.status}</p>
          </button>

          <button 
            onClick={() => onNavigate('settings')}
            className="group p-4 bg-white rounded-xl border border-stone-200/60 hover:border-emerald-200 hover:shadow-md transition-all text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Key className="w-5 h-5 text-purple-600" />
            </div>
            <p className="font-medium text-stone-800 text-sm">Sécurité</p>
            <p className="text-xs text-stone-400">Mot de passe</p>
          </button>
        </div>
      </div>

      {/* Right Column - Status & Limits */}
      <div className="space-y-6">
        {/* Subscription Status */}
        <Card className="border-stone-200/60 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-stone-800">État de l'abonnement</h4>
              <Badge className={
                subscription?.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                subscription?.status === 'TRIAL' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                'bg-stone-100 text-stone-700'
              }>
                {subscription?.status === 'ACTIVE' ? 'Actif' : subscription?.status}
              </Badge>
            </div>
            
            {daysUntilBilling !== null && (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-stone-500">Prochaine facturation</span>
                  <span className="font-medium text-stone-800">
                    {daysUntilBilling > 0 ? `Dans ${daysUntilBilling} jours` : 'Aujourd\'hui'}
                  </span>
                </div>
                <Progress 
                  value={Math.max(0, Math.min(100, ((30 - daysUntilBilling) / 30) * 100))} 
                  className="h-2"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Usage Limits Card */}
        <Card className="border-stone-200/60 shadow-sm">
          <CardContent className="p-6">
            <h4 className="font-semibold text-stone-800 mb-4">Utilisation</h4>
            
            {/* Riads Usage */}
            <div className="mb-5">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-stone-600 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-amber-500" />
                  Riads
                </span>
                <span className="font-semibold">
                  {accountLimits?.riads?.current || limits?.current_tenants || 0} / {accountLimits?.riads?.unlimited ? '∞' : (accountLimits?.riads?.max || limits?.max_tenants || 1)}
                </span>
              </div>
              {!accountLimits?.riads?.unlimited && (
                <Progress 
                  value={accountLimits?.riads?.percentage || ((limits?.current_tenants / limits?.max_tenants) * 100) || 0} 
                  className={`h-2 ${accountLimits?.riads?.limit_reached ? '[&>div]:bg-amber-500' : ''}`}
                />
              )}
              {accountLimits?.riads?.limit_reached && (
                <p className="text-xs text-amber-600 flex items-center gap-1 mt-2">
                  <AlertCircle className="w-3 h-3" />
                  Limite atteinte
                </p>
              )}
              {accountLimits?.riads?.remaining > 0 && !accountLimits?.riads?.unlimited && (
                <p className="text-xs text-stone-400 mt-1">
                  {accountLimits.riads.remaining} disponible(s)
                </p>
              )}
            </div>

            <Separator className="my-4" />

            {/* Users Usage */}
            <div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-stone-600 flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-500" />
                  Utilisateurs
                </span>
                <span className="font-semibold">
                  {accountLimits?.users?.current || 0} / {accountLimits?.users?.unlimited ? '∞' : (accountLimits?.users?.max || plan?.max_users || 5)}
                </span>
              </div>
              {!accountLimits?.users?.unlimited && (
                <Progress 
                  value={accountLimits?.users?.percentage || 0} 
                  className={`h-2 ${accountLimits?.users?.limit_reached ? '[&>div]:bg-red-500' : ''}`}
                />
              )}
              {accountLimits?.users?.limit_reached && (
                <p className="text-xs text-red-600 flex items-center gap-1 mt-2">
                  <AlertCircle className="w-3 h-3" />
                  Limite dépassée
                </p>
              )}
            </div>

            {(accountLimits?.riads?.limit_reached || accountLimits?.users?.limit_reached) && (
              <Button 
                className="w-full mt-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                onClick={() => onNavigate('subscription')}
              >
                <Zap className="w-4 h-4 mr-2" />
                Upgrade mon plan
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Modules */}
        {plan?.allowed_modules && Object.keys(plan.allowed_modules).length > 0 && (
          <Card className="border-stone-200/60 shadow-sm">
            <CardContent className="p-6">
              <h4 className="font-semibold text-stone-800 mb-4">Modules inclus</h4>
              <div className="flex flex-wrap gap-2">
                {Object.entries(plan.allowed_modules)
                  .filter(([, enabled]) => enabled)
                  .slice(0, 8)
                  .map(([module]) => (
                    <Badge key={module} variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      <Check className="w-3 h-3 mr-1" />
                      {module}
                    </Badge>
                  ))}
                {Object.values(plan.allowed_modules).filter(Boolean).length > 8 && (
                  <Badge variant="outline" className="bg-stone-50">
                    +{Object.values(plan.allowed_modules).filter(Boolean).length - 8}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ============ SUBSCRIPTION TAB ============
const SubscriptionTab = ({ subscription, limits }) => {
  if (!subscription) {
    return (
      <Card className="border-stone-200/60">
        <CardContent className="py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
          </div>
          <h3 className="text-lg font-semibold text-stone-800 mb-2">Aucun abonnement actif</h3>
          <p className="text-stone-500 mb-6">Contactez le support pour activer votre abonnement.</p>
          <Button className="bg-emerald-600 hover:bg-emerald-700">Contacter le support</Button>
        </CardContent>
      </Card>
    );
  }

  const plan = subscription.plan;
  const billingCycle = subscription.billing_cycle || 'MONTHLY';

  return (
    <div className="space-y-6">
      {/* Plan Card */}
      <Card className="border-stone-200/60 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-10 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <Badge className="bg-white/20 text-white border-white/30 mb-3">
                {subscription.status === 'ACTIVE' ? 'Actif' : subscription.status}
              </Badge>
              <h2 className="text-3xl font-bold mb-2">{plan?.name || 'Standard'}</h2>
              <p className="text-emerald-100">{plan?.description}</p>
            </div>
            <div className="text-center md:text-right">
              <div className="text-5xl font-bold mb-1">
                {(subscription.price || 0).toLocaleString()}
                <span className="text-lg font-normal ml-1">{subscription.currency}</span>
              </div>
              <p className="text-emerald-100">
                {billingCycle === 'YEARLY' ? 'par an' : 'par mois'}
              </p>
            </div>
          </div>
        </div>
        
        <CardContent className="p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-stone-500 mb-1">Date de début</p>
              <p className="font-semibold text-stone-800">
                {subscription.start_date 
                  ? format(new Date(subscription.start_date), 'dd MMMM yyyy', { locale: fr })
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-stone-500 mb-1">Prochaine facturation</p>
              <p className="font-semibold text-stone-800">
                {subscription.next_billing_date 
                  ? format(new Date(subscription.next_billing_date), 'dd MMMM yyyy', { locale: fr })
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-stone-500 mb-1">Cycle</p>
              <p className="font-semibold text-stone-800">
                {billingCycle === 'YEARLY' ? 'Annuel' : 'Mensuel'}
              </p>
            </div>
            <div>
              <p className="text-sm text-stone-500 mb-1">Renouvellement</p>
              <p className="font-semibold text-stone-800 flex items-center gap-1">
                {subscription.auto_renew ? (
                  <>
                    <Check className="w-4 h-4 text-emerald-500" />
                    Automatique
                  </>
                ) : (
                  <>
                    <X className="w-4 h-4 text-stone-400" />
                    Manuel
                  </>
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Limits */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-stone-200/60 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Riads / Tenants</p>
                <p className="text-2xl font-bold text-stone-800">
                  {limits?.current_tenants || 0} <span className="text-lg font-normal text-stone-400">/ {limits?.max_tenants === 0 ? '∞' : limits?.max_tenants}</span>
                </p>
              </div>
            </div>
            {limits?.max_tenants > 0 && (
              <Progress value={(limits?.current_tenants / limits?.max_tenants) * 100} className="h-2" />
            )}
          </CardContent>
        </Card>

        <Card className="border-stone-200/60 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Utilisateurs max</p>
                <p className="text-2xl font-bold text-stone-800">
                  {plan?.max_users === 0 ? '∞' : plan?.max_users || 5}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modules */}
      {plan?.allowed_modules && (
        <Card className="border-stone-200/60 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Modules inclus</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(plan.allowed_modules).map(([module, enabled]) => (
                <div 
                  key={module} 
                  className={`flex items-center gap-2 p-3 rounded-lg border ${
                    enabled 
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
                      : 'bg-stone-50 border-stone-200 text-stone-400'
                  }`}
                >
                  {enabled ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  <span className="text-sm font-medium capitalize">{module}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============ INVOICES TAB ============
const InvoicesTab = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async (page = 1) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/account/invoices?page=${page}`);
      setInvoices(data.invoices);
      setPagination(data.pagination);
    } catch (error) {
      toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    setDownloading(invoiceId);
    try {
      const response = await api.get(`/account/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture-${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF téléchargé');
    } catch (error) {
      console.error('Download PDF error:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloading(null);
    }
  };

  const getStatusStyle = (status) => {
    const styles = {
      DRAFT: { bg: 'bg-stone-100', text: 'text-stone-600', label: 'Brouillon' },
      SENT: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Envoyée' },
      PAID: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Payée' },
      OVERDUE: { bg: 'bg-red-100', text: 'text-red-700', label: 'En retard' },
      CANCELLED: { bg: 'bg-stone-100', text: 'text-stone-500', label: 'Annulée' }
    };
    return styles[status] || styles.DRAFT;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <Card className="border-stone-200/60 shadow-sm">
      <CardHeader className="border-b border-stone-100">
        <CardTitle>Historique des factures</CardTitle>
        <CardDescription>Consultez et téléchargez vos factures</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {invoices.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-stone-400" />
            </div>
            <h3 className="font-semibold text-stone-800 mb-2">Aucune facture</h3>
            <p className="text-stone-500">Vos factures apparaîtront ici</p>
          </div>
        ) : (
          <div className="divide-y divide-stone-100">
            {invoices.map(invoice => {
              const status = getStatusStyle(invoice.status);
              return (
                <div key={invoice.id} className="flex items-center justify-between p-5 hover:bg-stone-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl ${status.bg} flex items-center justify-center`}>
                      <FileText className={`w-6 h-6 ${status.text}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800">{invoice.invoice_number}</p>
                      <p className="text-sm text-stone-500">
                        {invoice.issue_date && format(new Date(invoice.issue_date), 'dd MMMM yyyy', { locale: fr })}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <Badge className={`${status.bg} ${status.text} border-0`}>
                      {status.label}
                    </Badge>
                    <div className="text-right">
                      <p className="font-bold text-stone-800">
                        {invoice.total_amount?.toLocaleString()} {invoice.currency}
                      </p>
                      {invoice.due_date && invoice.status !== 'PAID' && (
                        <p className="text-xs text-stone-400">
                          Éch. {format(new Date(invoice.due_date), 'dd/MM/yyyy')}
                        </p>
                      )}
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDownloadPdf(invoice.id, invoice.invoice_number)}
                      disabled={downloading === invoice.id}
                      className="gap-2"
                    >
                      {downloading === invoice.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      PDF
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {pagination.pages > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-stone-100">
            {Array.from({ length: pagination.pages }, (_, i) => (
              <Button
                key={i + 1}
                variant={pagination.page === i + 1 ? "default" : "outline"}
                size="sm"
                onClick={() => fetchInvoices(i + 1)}
              >
                {i + 1}
              </Button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============ RIADS TAB ============
const RiadsTab = ({ limits, onUpdate }) => {
  const [riads, setRiads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newRiad, setNewRiad] = useState({ name: '', city: '', address: '', phone: '', email: '' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchRiads();
  }, []);

  const fetchRiads = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/account/riads');
      setRiads(data.riads);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newRiad.name.trim()) {
      toast.error('Le nom du riad est obligatoire');
      return;
    }
    
    setCreating(true);
    try {
      await api.post('/account/riads', newRiad);
      toast.success('Riad créé avec succès');
      setShowCreateDialog(false);
      setNewRiad({ name: '', city: '', address: '', phone: '', email: '' });
      fetchRiads();
      onUpdate();
    } catch (error) {
      if (error.response?.data?.code === 'TENANT_LIMIT_REACHED') {
        toast.error(error.response.data.error);
      } else {
        toast.error('Erreur lors de la création');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleOpenRiad = (riad) => {
    localStorage.setItem('currentRiadId', riad.id);
    navigate('/');
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-stone-200/60 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-800">Mes Riads</h3>
                <p className="text-sm text-stone-500">
                  {riads.length} / {limits?.max_tenants === 0 ? '∞' : limits?.max_tenants} riad(s) utilisé(s)
                </p>
              </div>
            </div>
            {limits?.can_create ? (
              <Button onClick={() => setShowCreateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                <Plus className="w-4 h-4" />
                Nouveau riad
              </Button>
            ) : (
              <div className="flex items-center gap-3">
                <Badge className="bg-amber-100 text-amber-700 border-amber-200">Limite atteinte</Badge>
                <Button variant="outline" size="sm">Upgrade</Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Riads Grid */}
      {riads.length === 0 ? (
        <Card className="border-stone-200/60 shadow-sm">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-stone-400" />
            </div>
            <h3 className="font-semibold text-stone-800 mb-2">Aucun riad</h3>
            <p className="text-stone-500 mb-6">Créez votre premier riad pour commencer</p>
            {limits?.can_create && (
              <Button onClick={() => setShowCreateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                <Plus className="w-4 h-4" />
                Créer un riad
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {riads.map(riad => (
            <Card key={riad.id} className="border-stone-200/60 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
              <CardContent className="p-0">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      {riad.logo ? (
                        <img src={riad.logo} alt={riad.name} className="w-14 h-14 rounded-xl object-cover" />
                      ) : (
                        <div className="w-14 h-14 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center">
                          <Building2 className="w-7 h-7 text-amber-600" />
                        </div>
                      )}
                      <div>
                        <h4 className="font-semibold text-stone-800 text-lg">{riad.name}</h4>
                        {riad.city && <p className="text-sm text-stone-500">{riad.city}</p>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge className={
                        riad.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                        riad.status === 'SUSPENDED' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                        'bg-stone-100 text-stone-600'
                      }>
                        {riad.status === 'ACTIVE' ? 'Actif' : riad.status}
                      </Badge>
                      <Badge variant="outline" className={
                        riad.setup_state === 'COMPLETED' ? 'bg-blue-50 text-blue-600 border-blue-200' :
                        'bg-amber-50 text-amber-600 border-amber-200'
                      }>
                        {riad.setup_state === 'COMPLETED' ? 'Configuré' : 'À configurer'}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="px-6 py-4 bg-stone-50 border-t border-stone-100 flex items-center justify-between">
                  <p className="text-xs text-stone-400">
                    Créé le {format(new Date(riad.createdAt), 'dd/MM/yyyy')}
                  </p>
                  <Button onClick={() => handleOpenRiad(riad)} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                    Ouvrir
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Créer un nouveau riad</DialogTitle>
            <DialogDescription>Ajoutez un nouveau riad à votre compte</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Nom du riad *</Label>
              <Input
                value={newRiad.name}
                onChange={e => setNewRiad({...newRiad, name: e.target.value})}
                placeholder="Ex: Riad des Étoiles"
                required
                className="mt-1.5"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ville</Label>
                <Input
                  value={newRiad.city}
                  onChange={e => setNewRiad({...newRiad, city: e.target.value})}
                  placeholder="Marrakech"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={newRiad.phone}
                  onChange={e => setNewRiad({...newRiad, phone: e.target.value})}
                  placeholder="+212..."
                  className="mt-1.5"
                />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newRiad.email}
                onChange={e => setNewRiad({...newRiad, email: e.target.value})}
                placeholder="contact@riad.ma"
                className="mt-1.5"
              />
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={creating} className="bg-emerald-600 hover:bg-emerald-700">
                {creating && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
                Créer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ SETTINGS TAB ============
const SettingsTab = ({ account, owner, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: account?.name || '',
    email: account?.email || '',
    phone: account?.phone || '',
    company: account?.company || '',
    billing_email: account?.billing_email || '',
    billing_address: account?.billing_address || ''
  });
  
  // Password
  const [showPassword, setShowPassword] = useState({ current: false, new: false, confirm: false });
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingPassword, setSavingPassword] = useState(false);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/account', formData);
      toast.success('Profil mis à jour');
      setEditing(false);
      onUpdate();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (passwords.new_password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setSavingPassword(true);
    try {
      await api.put('/account/password', passwords);
      toast.success('Mot de passe modifié');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Profile */}
      <Card className="border-stone-200/60 shadow-sm">
        <CardHeader className="border-b border-stone-100">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Informations du compte</CardTitle>
            <Button 
              variant={editing ? "default" : "outline"}
              size="sm"
              onClick={() => editing ? handleSaveProfile() : setEditing(true)}
              disabled={saving}
              className={editing ? "bg-emerald-600 hover:bg-emerald-700" : ""}
            >
              {saving && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
              {editing ? 'Enregistrer' : 'Modifier'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div>
            <Label>Nom du compte</Label>
            {editing ? (
              <Input 
                value={formData.name} 
                onChange={e => setFormData({...formData, name: e.target.value})}
                className="mt-1.5"
              />
            ) : (
              <p className="mt-1 text-stone-800 font-medium">{account?.name}</p>
            )}
          </div>
          
          <div>
            <Label>Email</Label>
            {editing ? (
              <Input 
                type="email"
                value={formData.email} 
                onChange={e => setFormData({...formData, email: e.target.value})}
                className="mt-1.5"
              />
            ) : (
              <p className="mt-1 text-stone-800">{account?.email || '-'}</p>
            )}
          </div>
          
          <div>
            <Label>Téléphone</Label>
            {editing ? (
              <Input 
                value={formData.phone} 
                onChange={e => setFormData({...formData, phone: e.target.value})}
                className="mt-1.5"
              />
            ) : (
              <p className="mt-1 text-stone-800">{account?.phone || '-'}</p>
            )}
          </div>
          
          <div>
            <Label>Société</Label>
            {editing ? (
              <Input 
                value={formData.company} 
                onChange={e => setFormData({...formData, company: e.target.value})}
                className="mt-1.5"
              />
            ) : (
              <p className="mt-1 text-stone-800">{account?.company || '-'}</p>
            )}
          </div>

          {editing && (
            <Button variant="outline" onClick={() => setEditing(false)} className="w-full mt-4">
              Annuler
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Password */}
      <Card className="border-stone-200/60 shadow-sm">
        <CardHeader className="border-b border-stone-100">
          <CardTitle className="text-lg">Sécurité</CardTitle>
          <CardDescription>Modifiez votre mot de passe</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label>Mot de passe actuel</Label>
              <div className="relative mt-1.5">
                <Input
                  type={showPassword.current ? 'text' : 'password'}
                  value={passwords.current_password}
                  onChange={e => setPasswords({...passwords, current_password: e.target.value})}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  onClick={() => setShowPassword({...showPassword, current: !showPassword.current})}
                >
                  {showPassword.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <Separator />
            
            <div>
              <Label>Nouveau mot de passe</Label>
              <div className="relative mt-1.5">
                <Input
                  type={showPassword.new ? 'text' : 'password'}
                  value={passwords.new_password}
                  onChange={e => setPasswords({...passwords, new_password: e.target.value})}
                  minLength={6}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  onClick={() => setShowPassword({...showPassword, new: !showPassword.new})}
                >
                  {showPassword.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-stone-400 mt-1">Minimum 6 caractères</p>
            </div>
            
            <div>
              <Label>Confirmer</Label>
              <div className="relative mt-1.5">
                <Input
                  type={showPassword.confirm ? 'text' : 'password'}
                  value={passwords.confirm_password}
                  onChange={e => setPasswords({...passwords, confirm_password: e.target.value})}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  onClick={() => setShowPassword({...showPassword, confirm: !showPassword.confirm})}
                >
                  {showPassword.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <Button type="submit" disabled={savingPassword} className="w-full bg-emerald-600 hover:bg-emerald-700">
              {savingPassword && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
              Changer le mot de passe
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountPage;
